import { useEffect, useMemo, useState } from "react";
import { Bell } from "lucide-react";

import FileItem from "./file-item";
import { useTranslation } from "react-i18next";
import UploadFileDialog from "./upload-modal";
import { Skeleton } from "@/components/ui/skeleton";
import { PatientFile } from "@/types/public";
import {
  selectCurrentRoom,
  setShowFileNotif,
  setUnSeenFiles,
} from "@/context/features/room/roomSlice";
import { selectCurrentAuth } from "@/context/features/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import { useNotifyFileChange } from "@/hooks/livekit/useNotifyFileChange";
import { useReceiveFileChange } from "@/hooks/livekit/useReceiveFileChange";
import { Role } from "@/types/role.enum";
import { Badge } from "@/components/ui/badge";
import { useGetPatientDocumentLazyQuery } from "@/graphql/generated";

export default function FileList() {
  const { user } = useSelector(selectCurrentAuth);
  const { focusedVid, showFileNotif, activeTab } =
    useSelector(selectCurrentRoom);
  const { livekitContext } = useLivekitContext();
  const { room } = livekitContext ?? {};
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [latencyDeleteId, setLatencyDeleteId] = useState<string | null>();

  const [getDocs, { data, loading, error, refetch, called, networkStatus }] =
    useGetPatientDocumentLazyQuery({
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      notifyOnNetworkStatusChange: true,
    });

  const effectiveVid = useMemo(() => {
    if (focusedVid && user?.role !== Role.GUEST) return focusedVid.toString();
    if (user?.role === Role.GUEST && user.visit_guest) return user.visit_guest;
    return undefined;
  }, [focusedVid, user?.role, user?.visit_guest]);

  useEffect(() => {
    // console.log({ effectiveVid });
    if (activeTab === "files" && effectiveVid) {
      getDocs({ variables: { vid: effectiveVid } });
    }
  }, [activeTab, effectiveVid, getDocs]);

  const { notifyFileChange } = useNotifyFileChange({
    user: room?.localParticipant,
    room,
  });

  useReceiveFileChange("file", room, (data) => {
    if (data.type === "delete") setLatencyDeleteId(data.fileId);
    if (called) refetch();
  });

  const files = data?.room.getPatientDocument ?? [];

  const isInitialLoad = (!called && loading) || networkStatus === 1;
  const isRefetching = networkStatus === 4;

  return (
    <div
      className="bg-background overflow-auto flex flex-col h-full gap-3 border-t "
      onMouseEnter={() => {
        dispatch(setUnSeenFiles(0));
        dispatch(setShowFileNotif(false));
      }}
    >
      <div className="flex items-center justify-between p-2 ">
        <div className="flex items-center gap-2">
          <h3 className=" font-bold text-primary">{t("files_list")}</h3>
          <div className="flex items-center gap-2">
            {showFileNotif && (
              <Badge
                className="bg-pink-200 rounded-full p-1 shadow animate-pulse-glow"
                onClick={() => {
                  dispatch(setShowFileNotif(false));
                  dispatch(setUnSeenFiles(0));
                }}
              >
                <Bell size={15} className=" text-pink-700 " />
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 p-2 overflow-auto">
        {user?.role === Role.GUEST && (
          <div className=" text-orange-500 border border-orange-500  rounded-md bg-orange-500/10 shadow w-fit text-center p-1">
            <span className="text-sm">{t("guest_file_info")}</span>
          </div>
        )}

        {/* Initial load: show 3 skeletons at top */}
        {!isInitialLoad && error && (
          <div className="text-sm text-red-500 w-full text-center p-2">
            <span className="border border-red-500 p-1 px-2 rounded-md bg-red-500/10 shadow">
              {t("error_loading_files") || "Failed to load files."}
            </span>
          </div>
        )}

        {!isInitialLoad &&
          !error &&
          activeTab === "files" &&
          files.length === 0 && (
            <div className="text-sm text-orange-500 w-full text-center p-2 mt-10">
              <span className="border border-orange-500 p-1 px-2 rounded-md bg-orange-500/10 shadow">
                {!effectiveVid && user?.role === Role.HOST
                  ? t("focus_on_patients")
                  : t("no_files_found")}
              </span>
            </div>
          )}

        {/* Files */}
        {!error &&
          files
            .filter((item) => item.id !== latencyDeleteId)
            .map((item) => (
              <FileItem
                key={item.id}
                {...(item as PatientFile)}
                onRemoveFile={() => {
                  notifyFileChange("file", item.id, "delete");
                }}
              />
            ))}

        {isInitialLoad && (
          <>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </>
        )}
        {/* Refetching */}
        {isRefetching && files.length > 0 && (
          <Skeleton className="h-10 w-full" />
        )}
      </div>
      <div className="flex items-center justify-center p-4">
        <UploadFileDialog refetch={() => refetch()} />
      </div>
    </div>
  );
}
